// File: @/components/media/media-library/MediaCard.tsx
import {useCallback} from 'react';
import {View, Text, StyleSheet} from 'react-native';
import {Image} from 'expo-image';
import {formatDuration} from '@/lib/utils';
import {useCurrentTheme} from '@/theming/useCurrentTheme';
import {AppDataMediaItem} from '@/data/app-data-interfaces';
import {ThemeType} from '@/theming/baseTheme';
import {PlayButton} from '@/components/common/PlayButton';
import {BaseCard} from '@/components/common/BaseCard';
import {OpenButton} from "@/components/common/OpenButton";

interface MediaCardProps {
    item: AppDataMediaItem;
    onPress?: (item: AppDataMediaItem) => void;
}

export const MediaCard = ({item, onPress}: MediaCardProps) => {
    const {theme} = useCurrentTheme();
    const styles = createStyles(theme);

    const handlePress = useCallback(() => {
        onPress?.(item);
    }, [item, onPress]);

    const blurhash = item.imageBlurhash && item.imageBlurhash !== 'undefined' ? item.imageBlurhash : 'LKO2?U%2Tw=w]~RBVZRi};RPxuwH';

    return (
        <BaseCard onPress={onPress ? handlePress : undefined} activeOpacity={0.7} variant="flex">
            <View style={styles.mediaImageContainer}>
                <Image
                    source={{uri: item.image.md}}
                    placeholder={{blurhash}}
                    style={styles.mediaImage}
                    transition={200}
                    contentFit="cover"
                />
                <View style={styles.mediaType}>
                    <Text style={styles.mediaTypeText}>{item.type.toUpperCase()}</Text>
                </View>
                {(item.type === 'video' || item.type === 'audio') && onPress && (
                    <>
                        <PlayButton
                            size={40}
                            style={styles.playButtonOverlay}
                            onPress={handlePress}
                        />
                        {item.duration !== 0 && (
                            <View style={styles.durationBadge}>
                                <Text style={styles.durationText}>
                                    {formatDuration(item.duration)}
                                </Text>
                            </View>
                        )}
                    </>
                )}
                {item.type !== 'video' && item.type !== 'audio' && onPress && (
                    <OpenButton
                        size={40}
                        style={styles.playButtonOverlay}
                        onPress={handlePress}
                    />
                )}
            </View>
            <View style={styles.mediaInfo}>
                <Text style={styles.mediaTitle} numberOfLines={1}>{item.name}</Text>
                <Text style={styles.mediaDescription} numberOfLines={1}>
                    {item.shortDescription}
                </Text>
            </View>
        </BaseCard>
    );
};

const createStyles = (theme: ThemeType) => StyleSheet.create({
    mediaImageContainer: {
        height: 120,
        borderTopLeftRadius: theme.borderRadius.md,
        borderTopRightRadius: theme.borderRadius.md,
        position: 'relative',
        overflow: 'hidden',
    },

    mediaImage: {
        width: '100%',
        height: '100%',
    },

    playButtonOverlay: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: [{translateX: -20}, {translateY: -20}],
    },

    durationBadge: {
        position: 'absolute',
        bottom: theme.spacing.xs,
        right: theme.spacing.xs,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        paddingHorizontal: theme.spacing.xs,
        paddingVertical: 2,
        borderRadius: theme.borderRadius.sm,
    },

    durationText: {
        color: 'white',
        fontSize: theme.fontSize.xs,
        fontWeight: '600',
        lineHeight: 14,
    },

    mediaType: {
        position: 'absolute',
        top: theme.spacing.xs,
        right: theme.spacing.xs,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        paddingHorizontal: theme.spacing.xs,
        paddingVertical: 2,
        borderRadius: theme.borderRadius.sm,
    },

    mediaTypeText: {
        color: 'white',
        fontSize: theme.fontSize.xs,
        fontWeight: '600',
    },

    mediaInfo: {
        padding: theme.spacing.sm,
    },

    mediaTitle: {
        fontSize: theme.fontSize.sm,
        fontWeight: '600',
        color: theme.colors.text,
        marginBottom: theme.spacing.xs,
    },

    mediaDescription: {
        fontSize: theme.fontSize.xs,
        color: theme.colors.textSecondary,
        lineHeight: 16,
    },
});
